import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { getPendingReviewRequests, assignRequestsToCommunique } from '../../../services/serviceRequestService';
import { createCommunique, getPagedCommuniques } from '../../../services/communiqueService';
import { ServiceRequest } from '../../../types/serviceRequest';
import { Communique } from '../../../types/communique';
import pageStyles from '../../Page.module.css';
import formStyles from '../../../components/forms/Form.module.css';

// نوع بيانات نموذج إنشاء البلاغ
interface NewCommuniqueFormInputs {
  communique_number: string;
  meeting_date: string;
  summary?: string;
}

const ManageCommuniquePage: React.FC = () => {
  const [pendingRequests, setPendingRequests] = useState<ServiceRequest[]>([]);
  const [communiques, setCommuniques] = useState<Communique[]>([]);
  const [selectedRequestIds, setSelectedRequestIds] = useState<number[]>([]);
  const [selectedCommuniqueId, setSelectedCommuniqueId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<NewCommuniqueFormInputs>();

  // جلب البيانات عند تحميل الصفحة
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const requestsPromise = getPendingReviewRequests();
      const communiquesPromise = getPagedCommuniques({ page: 0, pageIndex: 0, pageSize: 100 });
      
      const [requestsRes, communiquesRes] = await Promise.all([requestsPromise, communiquesPromise]);
      
      setPendingRequests(requestsRes);
      setCommuniques(communiquesRes.items);
    } catch (error) {
      toast.error('فشل في جلب البيانات اللازمة.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // التعامل مع تحديد الطلبات
  const handleSelectRequest = (requestId: number) => {
    setSelectedRequestIds(prev =>
      prev.includes(requestId)
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  // إرسال نموذج إنشاء بلاغ جديد
  const onCommuniqueSubmit: SubmitHandler<NewCommuniqueFormInputs> = async (data) => {
    const promise = createCommunique(data);
    toast.promise(promise, {
      loading: 'جاري إنشاء البلاغ...',
      success: () => {
        reset();
        fetchData(); // إعادة تحميل البيانات
        return 'تم إنشاء البلاغ بنجاح!';
      },
      error: (err: any) => err.response?.data?.message || 'فشل في إنشاء البلاغ.',
    });
  };

  // التعامل مع إحالة الطلبات المحددة إلى بلاغ
  const handleAssignRequests = async () => {
    if (selectedRequestIds.length === 0) {
      toast.error('يرجى تحديد طلب واحد على الأقل.');
      return;
    }
    if (!selectedCommuniqueId) {
      toast.error('يرجى اختيار بلاغ لإحالة الطلبات إليه.');
      return;
    }

    const promise = assignRequestsToCommunique(parseInt(selectedCommuniqueId), selectedRequestIds);
    toast.promise(promise, {
      loading: 'جاري إحالة الطلبات...',
      success: () => {
        setSelectedRequestIds([]);
        fetchData(); // إعادة تحميل البيانات
        return 'تمت إحالة الطلبات بنجاح!';
      },
      error: (err: any) => err.response?.data?.message || 'فشل في إحالة الطلبات.',
    });
  };

  if (isLoading) {
    return <div className={pageStyles.loading}>جاري تحميل البيانات...</div>;
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.pageHeader}>
        <h1>إدارة البلاغات وإحالة الطلبات</h1>
      </div>

      {/* قسم الطلبات الجاهزة للمراجعة */}
      <div className={pageStyles.pageContent}>
        <h2>الطلبات الجاهزة للإحالة ({pendingRequests.length})</h2>
        <div className={formStyles.tableContainer}>
          <table className={formStyles.table}>
            <thead>
              <tr>
                <th><input type="checkbox" onChange={(e) => setSelectedRequestIds(e.target.checked ? pendingRequests.map(r => r.id) : [])} /></th>
                <th>رقم الطلب</th>
                <th>مقدم الطلب</th>
                <th>الخدمة المطلوبة</th>
                <th>تاريخ التقديم</th>
              </tr>
            </thead>
            <tbody>
              {pendingRequests.map(req => (
                <tr key={req.id}>
                  <td><input type="checkbox" checked={selectedRequestIds.includes(req.id)} onChange={() => handleSelectRequest(req.id)} /></td>
                  <td>{req.id}</td>
                  <td>{req.user?.email || 'غير معروف'}</td>
                  <td>{req.service?.name || 'غير معروف'}</td>
                  <td>{new Date(req.created_at).toLocaleDateString('ar-EG')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* قسم الإحالة وإنشاء بلاغ جديد */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* نموذج إحالة الطلبات */}
        <div className={pageStyles.pageContent}>
          <h2>إحالة الطلبات المحددة ({selectedRequestIds.length})</h2>
          <div className={formStyles.formField}>
            <label htmlFor="communiqueSelect">اختر البلاغ</label>
            <select
              id="communiqueSelect"
              value={selectedCommuniqueId}
              onChange={(e) => setSelectedCommuniqueId(e.target.value)}
              className={formStyles.input}
            >
              <option value="">-- اختر بلاغًا --</option>
              {communiques.map(c => (
                <option key={c.id} value={c.id}>{c.communique_number} - {new Date(c.meeting_date).toLocaleDateString('ar-EG')}</option>
              ))}
            </select>
          </div>
          <button onClick={handleAssignRequests} className={formStyles.submitButton} disabled={selectedRequestIds.length === 0 || !selectedCommuniqueId}>
            إحالة الطلبات المحددة
          </button>
        </div>

        {/* نموذج إنشاء بلاغ جديد */}
        <div className={pageStyles.pageContent}>
          <h2>أو قم بإنشاء بلاغ جديد</h2>
          <form onSubmit={handleSubmit(onCommuniqueSubmit)} className={formStyles.form}>
            <div className={formStyles.formField}>
              <label htmlFor="communique_number">رقم البلاغ</label>
              <input id="communique_number" {...register('communique_number', { required: 'رقم البلاغ مطلوب' })} className={formStyles.input} />
              {errors.communique_number && <p className={formStyles.error}>{errors.communique_number.message}</p>}
            </div>
            <div className={formStyles.formField}>
              <label htmlFor="meeting_date">تاريخ الاجتماع</label>
              <input id="meeting_date" type="date" {...register('meeting_date', { required: 'تاريخ الاجتماع مطلوب' })} className={formStyles.input} />
              {errors.meeting_date && <p className={formStyles.error}>{errors.meeting_date.message}</p>}
            </div>
            <div className={formStyles.formField}>
              <label htmlFor="summary">ملخص (اختياري)</label>
              <textarea id="summary" {...register('summary')} className={formStyles.input} />
            </div>
            <button type="submit" className={formStyles.submitButton}>إنشاء بلاغ</button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ManageCommuniquePage;
